import Select from '../components/ui/Select'
import TextInput from '../components/ui/TextInput'
import { useI18n } from '../i18n'
import { dispatchAccountAction } from '../runtimeBridge'
import { openExistingSyncKeyModal } from '../state/appUiStore'
import { viewPanelState } from '../state/appUiStore'
import {
   accountState,
   setAccountHeightInput,
   setAccountTargetRunEffortInput,
   setAccountTargetWeightInput,
} from '../state/accountStore'
import { selectUnitSystem, unitSystem } from '../state/unitSystemStore'
import { getExampleValue, getUnitLabel, normalizeUnitSystem } from '../utils/units'

/**
 * Renders one numeric profile field with its label and hint.
 *
 * @param props - Field id, label, value, hint, and input handler.
 * @returns The field element.
 */
const ProfileNumberField = (props: {
   id: string
   label: string
   value: string
   step: string
   placeholder: string
   hint: string
   onInput: (value: string) => void
}) => (
   <div>
      <label for={props.id} class="block text-sm font-medium text-ink-muted mb-2">
         {props.label}
      </label>
      <TextInput
         type="number"
         id={props.id}
         value={props.value}
         onInput={event => props.onInput(event.currentTarget.value)}
         min="0"
         step={props.step}
         placeholder={props.placeholder}
      />
      <p class="text-xs text-ink-faint mt-1">{props.hint}</p>
   </div>
)

/**
 * Renders the units/height/targets profile form.
 *
 * @returns The form element.
 */
const AccountProfileForm = () => {
   const t = useI18n()
   /**
    * Submits the profile form.
    *
    * @param event - The form submit event.
    */
   const handleSubmit = (event: SubmitEvent) => {
      event.preventDefault()
      dispatchAccountAction({
         action: 'save-profile',
         heightInput: accountState().heightInput,
         targetWeightInput: accountState().targetWeightInput,
         targetRunEffortInput: accountState().targetRunEffortInput,
      })
   }
   const example = (kind: 'length' | 'mass') =>
      t('e.g., {value}', { value: getExampleValue(kind, unitSystem()) })
   return (
      <form id="account-form" class="space-y-4" onSubmit={handleSubmit}>
         <div>
            <label
               for="account-unit-system"
               class="block text-sm font-medium text-ink-muted mb-2"
            >
               {t('Units')}
            </label>
            <Select
               id="account-unit-system"
               value={unitSystem()}
               onChange={event =>
                  selectUnitSystem(normalizeUnitSystem(event.currentTarget.value))
               }
            >
               <option value="metric">{t('Metric (kg, cm, km)')}</option>
               <option value="imperial">{t('Imperial (lb, in, mi)')}</option>
            </Select>
            <p class="text-xs text-ink-faint mt-1">
               {t('Applies to how measurements are shown and entered on every device.')}
            </p>
         </div>
         <ProfileNumberField
            id="account-height-input"
            label={t('Height ({unit})', { unit: getUnitLabel('length', unitSystem()) })}
            value={accountState().heightInput}
            step="0.1"
            placeholder={example('length')}
            hint={t('Add your height to unlock BMI insights on the Measurements tab.')}
            onInput={setAccountHeightInput}
         />
         <ProfileNumberField
            id="account-target-weight-input"
            label={t('Target Weight ({unit})', {
               unit: getUnitLabel('mass', unitSystem()),
            })}
            value={accountState().targetWeightInput}
            step="0.1"
            placeholder={example('mass')}
            hint={t("We'll highlight this goal across your weight charts.")}
            onInput={setAccountTargetWeightInput}
         />
         <ProfileNumberField
            id="account-target-run-effort-input"
            label={t('Monthly run effort target')}
            value={accountState().targetRunEffortInput}
            step="1"
            placeholder={t('e.g., {value}', { value: 40 })}
            hint={t(
               'Effort = distance (km) × a multiplier that doubles every 4 km/h: 1× at 6 km/h, 2× at 10, 4× at 14 (0 below 6 km/h). 2 km at 10 km/h scores 4. The target shows as a dashed line on the run effort chart.',
            )}
            onInput={setAccountTargetRunEffortInput}
         />
         <button
            type="submit"
            class="btn-accent w-full text-lg font-bold py-4 rounded-lg transition-all duration-300 ease-in-out text-white shadow-lg"
         >
            {t('Save Settings')}
         </button>
         <p id="account-status" class="text-sm text-ink-muted">
            {accountState().statusMessage ||
               t(
                  'Create an anonymous synchronization key to back up data, or input an existing key to restore it on this device.',
               )}
         </p>
      </form>
   )
}

/**
 * Renders the create/enter sync-key buttons.
 *
 * @returns The actions element.
 */
const AccountKeyActions = () => {
   const t = useI18n()
   return (
      <div
         id="account-key-actions"
         class="space-y-3"
         classList={{ hidden: !accountState().showKeyActions }}
      >
         <button
            id="account-create-key-btn"
            type="button"
            onClick={() => dispatchAccountAction({ action: 'create-sync-key' })}
            class="w-full rounded-lg border border-edge bg-surface-control text-ink px-4 py-3 text-sm font-semibold transition hover:bg-surface-control-hover focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-surface"
         >
            {t('Create new anonymous synchronization key')}
         </button>
         <button
            type="button"
            onClick={openExistingSyncKeyModal}
            class="w-full rounded-lg border border-edge bg-surface-raised text-ink-soft px-4 py-3 text-sm font-semibold transition hover:bg-surface-control focus:outline-none focus:ring-2 focus:ring-ink-muted focus:ring-offset-2 focus:ring-offset-surface"
         >
            {t('Input existing synchronization key')}
         </button>
      </div>
   )
}

/**
 * Renders the data export panel.
 *
 * @returns The panel element.
 */
const AccountDownloadPanel = () => {
   const t = useI18n()
   return (
      <div
         id="account-download-container"
         class="space-y-3 rounded-lg border border-edge bg-surface-raised p-4"
         classList={{ hidden: !accountState().hasDataToDelete }}
      >
         <p class="text-sm text-ink-soft">
            {t('Download your fasting, weight, and account data as a JSON file.')}
         </p>
         <button
            id="account-download-btn"
            type="button"
            onClick={() => dispatchAccountAction({ action: 'download-data' })}
            disabled={!accountState().hasDataToDelete || accountState().isDownloading}
            class="w-full rounded-lg border border-edge bg-surface-control text-ink-soft px-4 py-3 text-sm font-semibold transition hover:bg-surface-control-hover focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-surface"
         >
            {t('Download My Data')}
         </button>
         <p
            id="account-download-status"
            class="text-xs text-ink-muted"
            aria-live="polite"
         >
            {accountState().downloadStatusMessage}
         </p>
      </div>
   )
}

/**
 * Renders the delete-account panel.
 *
 * @returns The panel element.
 */
const AccountDeletePanel = () => {
   const t = useI18n()
   return (
      <div
         id="account-delete-container"
         class="space-y-3 rounded-lg border border-rose-500/40 bg-rose-500/10 p-4"
         classList={{ hidden: !accountState().hasDataToDelete }}
      >
         <p class="text-sm text-rose-200">
            {t(
               'Deleting your account erases all fasts, weight entries, goals, and preferences from this device and the synced database. This action cannot be undone.',
            )}
         </p>
         <button
            id="account-delete-btn"
            type="button"
            disabled={!accountState().hasDataToDelete}
            onClick={() => dispatchAccountAction({ action: 'delete-account' })}
            class="w-full rounded-lg border border-rose-500 px-4 py-3 bg-rose-600 text-sm font-semibold text-white transition hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:ring-offset-2 focus:ring-offset-surface"
         >
            {t('Delete Account Data')}
         </button>
      </div>
   )
}

/**
 * Renders the account tab (sync key, profile, export, delete).
 *
 * @returns The account view element.
 */
const AccountView = () => {
   const t = useI18n()
   return (
      <div
         id="account-view"
         class="view-panel w-full space-y-4 lg:col-span-2"
         data-view-state={viewPanelState('account')}
      >
         <div class="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start">
            <AccountProfileForm />
            <div class="space-y-4" id="account-side-panel">
               <AccountKeyActions />
               <div id="account-status-group" class="space-y-4">
                  <div
                     id="account-id-container"
                     classList={{ hidden: !accountState().userId }}
                  >
                     <p class="block text-sm font-medium text-ink-muted mb-2">
                        {t('Active Sync Key')}
                     </p>
                     <div
                        class="w-full rounded-control border border-edge-soft bg-surface-raised p-3 text-ink cursor-default select-all"
                        aria-readonly="true"
                     >
                        <code
                           id="account-id-text"
                           class="break-all text-base text-ink-soft"
                        >
                           {accountState().userId}
                        </code>
                     </div>
                  </div>
                  <button
                     id="account-create-qr-btn"
                     type="button"
                     onClick={() => dispatchAccountAction({ action: 'create-qr-code' })}
                     class="w-full rounded-lg border border-edge bg-surface-raised text-ink-soft px-4 py-3 text-sm font-semibold transition hover:bg-surface-control focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-surface"
                     classList={{ hidden: !accountState().showCreateQr }}
                  >
                     {t('Create QR code')}
                  </button>
                  <AccountDownloadPanel />
                  <AccountDeletePanel />
               </div>
            </div>
         </div>
      </div>
   )
}

export default AccountView
